/**
 * Las reglas de la biblioteca «Documentos y notas»: de qué TIPO es cada material, si cae dentro del
 * período elegido y cómo se arma la lista única.
 *
 * Vive aparte de la vista, sin React, por dos razones concretas:
 *
 *  1. **El chip y el ícono de la fila salen de la MISMA función.** El mockup los dibuja por
 *     separado (`TIPO_SVGS` para el ícono, `data-dt` para el filtro) y ahí no importa porque las
 *     filas están escritas a mano. Con material real, dos criterios distintos significan que una
 *     fila con el ícono de audio no aparece al filtrar «Audios transcriptos» — el usuario concluye
 *     que se borró.
 *  2. Se puede medir sin montar el modal.
 *
 * ⚠️ El archivo NO se llama `sourcesLibrary.js` a propósito: `SourcesLibrary.js` vive en esta misma
 * carpeta y en Windows los dos nombres son el MISMO archivo.
 */

import {DOC_SOURCE} from '../../memory/docConstants';
import {normalizeText} from '../../../../utils/textNormalize';

// Los cuatro cajones del mockup (`TIPO_LBL`), en el orden en que se dibujan los chips.
export const LIB_KIND = {
    DOC: 'doc',
    NOTE: 'nota',
    AUDIO: 'audio',
    MEETING: 'reunion',
};

export const LIB_ALL = 'all';

// Tope del listado del backend (`client_document_views.LIST_CAP`). Se espeja acá porque los filtros
// de esta vista corren en el CLIENTE: pasado el tope, «ningun material coincide con estos filtros»
// puede ser falso, y la lista tiene que poder decirlo.
export const LIB_LIST_CAP = 200;

export const LIB_KIND_ORDER = [LIB_KIND.DOC, LIB_KIND.NOTE, LIB_KIND.AUDIO, LIB_KIND.MEETING];

export const libKindLabelKey = (kind) => `ia_booster.sources.kind_${kind}`;

// Material tipeado por una persona (o dejado por el agente al cerrar una sesión): no hay archivo ni
// página del otro lado, es texto y nada más. `EMAIL` entra acá porque lo que se guarda es el cuerpo
// pegado, no un adjunto.
const NOTE_SOURCES = new Set([DOC_SOURCE.MANUAL, DOC_SOURCE.SESSION, DOC_SOURCE.EMAIL]);

/**
 * A qué cajón de la biblioteca pertenece un documento. Devuelve siempre uno de `LIB_KIND`: los
 * cuatro cubren todo el universo, así que ninguna fila puede quedar fuera de los chips (una fila
 * invisible en «Todo» sería material que el usuario cargó y no encuentra más).
 *
 * El orden de las preguntas ES la regla, y la primera es deliberada: **un audio es un audio aunque
 * sea la grabación de una reunión.** Se decidió así porque el ícono de la fila sale de acá mismo —
 * si «Reuniones» se quedara con las grabaciones, el chip «Audios transcriptos» dejaría afuera filas
 * que muestran un parlante, que es justo lo que el usuario está buscando cuando lo aprieta.
 *
 * Mapeo completo (campos del backend → cajón):
 *
 *   audio    · `file.mime_type` empieza con `audio/`, o `is_audio` (el backend ya sabe que el crudo
 *              es una transcripción, y lo sabe también para lo que entró sin archivo).
 *   reunion  · `source === MEETING` (la vía «Cargar reunión») o `document_kind` ===
 *              `MEETING_TRANSCRIPT` (lo que el clasificador reconoció, o alguien re-etiquetó).
 *   nota     · sin archivo y `source` ∈ {MANUAL, SESSION, EMAIL}: texto pegado, nota de la barra,
 *              resumen de sesión del MCP.
 *   doc      · todo el resto — UPLOAD (PDF/Word/imagen), DRIVE, URL (página web), NOTION, y
 *              cualquier MANUAL/SESSION que SÍ traiga archivo.
 */
export function libKindOf(doc) {
    if (!doc) return LIB_KIND.DOC;
    const mime = (doc.file && doc.file.mime_type) || '';
    if (doc.is_audio === true || String(mime).startsWith('audio/')) return LIB_KIND.AUDIO;
    if (doc.source === DOC_SOURCE.MEETING || doc.document_kind === 'MEETING_TRANSCRIPT') {
        return LIB_KIND.MEETING;
    }
    if (!doc.file && NOTE_SOURCES.has(doc.source)) return LIB_KIND.NOTE;
    return LIB_KIND.DOC;
}

/**
 * El día `YYYY-MM-DD` de un valor que llega en dos formas distintas, y por eso no se puede cortar
 * a diez caracteres y listo.
 *
 * `occurred_on` es un día puro (`2026-08-07`): no tiene hora, así que se usa tal cual.
 * `created_on` es un **timestamp con zona** (`2026-09-06T02:38:00Z`), y ahí `slice(0, 10)` devuelve
 * el día **en UTC**: un documento cargado a las 23:38 del 05/09 en un huso negativo —el nuestro—
 * se leía como 06/09. Por eso el timestamp pasa por `libIsoDay`, que lee el día en hora local.
 * Es el mismo desfasaje que ya estaba documentado en `libDateLabel` y en `libIsoDay`.
 */
function libDayOf(value) {
    const raw = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const parsed = new Date(raw);
    // Un valor que no se puede parsear cae al comportamiento viejo en vez de devolver «Invalid Date».
    return Number.isNaN(parsed.getTime()) ? raw.slice(0, 10) : libIsoDay(parsed);
}

/**
 * La fecha por la que ordena y filtra la lista, como `YYYY-MM-DD`.
 *
 * `occurred_on` primero: es cuándo PASÓ lo que el material cuenta (la fecha de la reunión), que es
 * por lo que alguien busca. `created_on` es cuándo se cargó, y sólo sirve de reemplazo.
 */
export function libDateOf(doc) {
    if (!doc) return '';
    if (doc.occurred_on) return libDayOf(doc.occurred_on);
    if (doc.created_on) return libDayOf(doc.created_on);
    return '';
}

/**
 * La fecha COMO SE LEE: `2026-08-07` → `07/08/2026`, que es lo que muestra el mockup (`.dprow .dm`).
 *
 * Va aparte de `libDateOf` a propósito: aquélla es la clave de ORDEN (`localeCompare`) y de RANGO
 * (`date < from`), y las dos dependen de que el formato siga siendo `YYYY-MM-DD`. Formatear ahí
 * rompería el orden de la lista y los dos filtros de período de una.
 *
 * Se parte el string en vez de usar `new Date(iso)`: ese constructor interpreta un `YYYY-MM-DD`
 * como medianoche **UTC**, así que en cualquier huso negativo —el nuestro— la fecha se muestra un
 * día antes.
 */
export function libDateLabel(isoDay) {
    const m = String(isoDay || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return isoDay || '';
    return `${m[3]}/${m[2]}/${m[1]}`;
}

export const LIB_PERIOD = {ALL: 'all', D30: '30', D90: '90'};

/** `YYYY-MM-DD` de un `Date`, en hora local — `toISOString()` desplaza el día al huso de Greenwich. */
export function libIsoDay(date) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * El rango `{from, to}` de un preset, anclado en HOY.
 *
 * El mockup ancla en una fecha fija (`2026-08-14`) porque su material está escrito a mano; acá el
 * ancla tiene que ser el día de hoy o «30 días» se vuelve una ventana histórica arbitraria.
 */
export function libPeriodRange(preset, today = new Date()) {
    if (preset !== LIB_PERIOD.D30 && preset !== LIB_PERIOD.D90) return {from: '', to: ''};
    const days = parseInt(preset, 10);
    const start = new Date(today.getTime());
    start.setDate(start.getDate() - (days - 1));
    return {from: libIsoDay(start), to: libIsoDay(today)};
}

/**
 * ¿Entra este material en el filtro?
 *
 * Un material SIN fecha (ni `occurred_on` ni `created_on` — pasa con lo más viejo) entra siempre:
 * esconderlo detrás de un rango que no puede cumplir sería borrarlo de la vista sin decirlo.
 */
/**
 * ¿El material coincide con lo que se escribió en el buscador? Se busca sobre el NOMBRE (el
 * título y, si es un archivo subido, el nombre del archivo), con acentos plegados y sin distinguir
 * mayúsculas: «reporte» encuentra «Reporte Julio 2026.pdf». Una búsqueda vacía coincide con todo
 * (es «sin filtro», no «nada»).
 *
 * NO se busca sobre el crudo: el listado es liviano a propósito (el texto no viaja) y buscar
 * dentro de los documentos es otra herramienta (`dashbo_search_client_memory`), no este filtro.
 */
export function libMatchesQuery(doc, query) {
    const needle = normalizeText(query).trim();
    if (!needle) return true;
    const names = [doc && doc.title, doc && doc.file && doc.file.name].filter(Boolean);
    return names.some((name) => normalizeText(name).includes(needle));
}

export function libMatches(doc, {kind = LIB_ALL, from = '', to = '', query = ''} = {}) {
    if (kind !== LIB_ALL && libKindOf(doc) !== kind) return false;
    if (!libMatchesQuery(doc, query)) return false;
    const date = libDateOf(doc);
    if (!date) return true;
    if (from && date < from) return false;
    if (to && date > to) return false;
    return true;
}

/**
 * El id de archivo de Drive dentro de un `web_view_link`, o `null`.
 *
 * Las dos formas que emite Drive: `/file/d/<id>/view` y `?id=<id>`.
 */
export function driveFileIdFrom(link) {
    if (!link) return null;
    const path = String(link).match(/\/d\/([a-zA-Z0-9_-]+)/);
    if (path) return path[1];
    const query = String(link).match(/[?&]id=([a-zA-Z0-9_-]+)/);
    return query ? query[1] : null;
}

/**
 * El id con el que un documento se casa con SU conexión de Drive, o `null` si no es de Drive.
 *
 * `external_id` primero: es el id del archivo en la fuente, es **columna** del modelo
 * (`ClientDocument._external_id`) y es exactamente lo que la conexión guarda. El `web_view_link`
 * queda de reemplazo mientras el backend no lo publique — los dos repos no deployan juntos.
 *
 * ⚠️ **Sólo se casan documentos cuya fuente ES Drive.** Un documento cargado desde un enlace guarda
 * en `web_view_link` la URL que pegó el usuario, y si esa URL es de Google Docs
 * (`docs.google.com/document/d/<id>`) el id sale igual. Sin este filtro, pegar el link de un archivo
 * que además está conectado por Drive producía: dos «Actualizar» en la misma fila, la etiqueta
 * «Drive» sobre algo que entró por URL, y la conexión perdiendo su fila propia (y con ella el
 * «Reconectar»). No es un hueco cross-agency —el match ocurre dentro del payload de un cliente—,
 * pero sí un cruce de dos cosas distintas.
 */
export function driveKeyOf(doc) {
    if (!doc || doc.source !== DOC_SOURCE.DRIVE) return null;
    return doc.external_id || driveFileIdFrom(doc.web_view_link);
}

/** ¿Esta conexión perdió el acceso? Un token revocado deja el documento vivo y la conexión muerta. */
export const connectionIsBroken = (conn) => !!conn
    && (conn.status === 'ERROR' || conn.status === 'REVOKED');

/**
 * ¿A esta conexión se le promete la relectura diaria? Sólo a una ACTIVA. El cron diario de las 5
 * (`trigger_source_connections_sync` del backend) saltea las pausadas; a las que tienen error las
 * sigue reintentando, pero mientras no se recuperen, prometerles «se actualiza todos los días» sería decir
 * algo que hoy no está pasando.
 */
export const connectionAutoSyncs = (conn) => !!conn && conn.status === 'ACTIVE';

// Cuánto se espera el primer sync de una conexión recién creada antes de dejar de mirarla. Arranca
// al conectar y tarda segundos; pasado esto, algo lo trabó y mirar más no lo destraba. Espejo de
// `ClientSourceConnection.FIRST_READ_WINDOW_MINUTES`, que es el que manda cuando viaja.
export const LIB_FIRST_READ_WINDOW_MS = 10 * 60 * 1000;

/**
 * ¿La conexión acaba de nacer y su primer sync todavía no corrió? Hasta entonces no hay documento y
 * la pantalla no tiene nada nuevo que mostrar por sí sola: hay que volver a preguntar (card pH4QPmhG —
 * sin esto la fila decía «Todavía sin leer» hasta un F5).
 *
 * ⚠️ `last_synced_at` quiere decir «el sync corrió», NO «el documento está leído»: el sync crea el
 * documento en cola y marca la conexión en el mismo paso. Lo que sigue a ese documento hasta READY
 * es `followDriveArrivals` (`SourcesLibrary`), no esto.
 *
 * Manda el `awaiting_first_read` del backend (calculado con el reloj del servidor); sin él —backend
 * viejo— se estima con `created_on` contra el reloj del navegador.
 */
export function connectionAwaitsFirstRead(conn, now = Date.now()) {
    if (!connectionAutoSyncs(conn) || conn.last_synced_at) return false;
    if (typeof conn.awaiting_first_read === 'boolean') return conn.awaiting_first_read;
    const created = Date.parse(conn.created_on || '');
    return !Number.isNaN(created) && now - created < LIB_FIRST_READ_WINDOW_MS;
}

/**
 * Cuándo se leyó por última vez una conexión, para «última: hoy 05:11». `null` sin fecha válida.
 *
 * `{kind: 'today' | 'yesterday' | 'date', time, date}`: el texto lo arma quien lo muestra, porque
 * «hoy» y «ayer» son copy. El día se compara en hora LOCAL (`libIsoDay`), igual que el resto de la
 * biblioteca: el cron corre 05:00 de Argentina, que en UTC ya es otro horario.
 */
export function libSyncWhen(iso, now = new Date()) {
    if (!iso) return null;
    const at = new Date(iso);
    if (Number.isNaN(at.getTime())) return null;
    const pad = (n) => String(n).padStart(2, '0');
    const time = `${pad(at.getHours())}:${pad(at.getMinutes())}`;
    const day = libIsoDay(at);
    const yesterday = new Date(now.getTime());
    yesterday.setDate(yesterday.getDate() - 1);
    if (day === libIsoDay(now)) return {kind: 'today', time};
    if (day === libIsoDay(yesterday)) return {kind: 'yesterday', time};
    return {kind: 'date', time, date: libDateLabel(day)};
}

/**
 * «Se actualiza todos los días · última: hoy 05:11», o sólo la promesa si todavía no hay lectura.
 * Lo usan la fila de la 2.1 y la del clásico: la misma conexión no puede decir dos cosas. Sin
 * género a propósito: en la 2.1 la fila es un archivo y en el clásico una conexión (review C-chicos-11).
 */
export function autoSyncLabel(t, conn, now = new Date()) {
    const when = libSyncWhen(conn && conn.last_synced_at, now);
    if (!when) return t('client_memory.sources.auto_sync_no_date');
    // La fecha lleva «/»: sin `escapeValue: false` i18next la escapa a «&#x2F;», en los dos pasos.
    // Son valores del sistema, no del usuario, y la fila los dibuja como texto de React.
    const raw = {interpolation: {escapeValue: false}};
    const date = t(`client_memory.sources.sync_${when.kind}`, {time: when.time, date: when.date, ...raw});
    return t('client_memory.sources.auto_sync', {date, ...raw});
}

/**
 * Una sola lista, ordenada de lo más nuevo a lo más viejo: los documentos, más las conexiones de
 * Drive que TODAVÍA no tienen documento.
 *
 * Las huérfanas existen y hay que mostrarlas: entre conectar un archivo y que el primer sync lo
 * traiga hay una ventana, y una conexión que nace rota (permiso revocado, archivo en la papelera)
 * no genera documento NUNCA. Sin su fila, esa conexión desaparece de la pantalla y con ella el
 * botón para reconectarla — que es el único arreglo posible.
 *
 * Al revés no hay riesgo de duplicar: un documento de Drive y su conexión son la misma cosa vista
 * dos veces, y el que manda es el documento (tiene el estado de extracción y los hechos).
 */
export function libEntries(documents = [], connections = []) {
    const byFileId = new Map();
    connections.forEach((conn) => {
        if (conn && conn.external_id) byFileId.set(conn.external_id, conn);
    });

    const linkedIds = new Set();
    const rows = documents.map((doc) => {
        const fileId = driveKeyOf(doc);
        // Sólo se marca como «ya tiene documento» la conexión que EXISTE: si el id no resuelve a
        // ninguna (el archivo se desconectó y el documento quedó), la conexión no está para perder
        // su fila, y marcarlo escondería una conexión viva de otro archivo con el mismo id.
        const connection = fileId ? byFileId.get(fileId) || null : null;
        if (connection) linkedIds.add(fileId);
        return {
            key: `doc-${doc.id}`,
            date: libDateOf(doc),
            document: doc,
            // La conexión que lo trajo: es de donde sale «Actualizar» — y, si perdió el acceso,
            // el «Reconectar». Ver `SourceRow`.
            connection,
        };
    });

    connections.forEach((conn) => {
        if (!conn || !conn.external_id || linkedIds.has(conn.external_id)) return;
        rows.push({
            key: `conn-${conn.id}`,
            date: conn.created_on ? String(conn.created_on).slice(0, 10) : '',
            document: null,
            connection: conn,
        });
    });

    // Sin fecha, al final: es material viejo del que no sabemos cuándo pasó, y encabezar la lista
    // con lo más incierto es lo contrario de lo que la lista promete.
    return rows.sort((a, b) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return b.date.localeCompare(a.date);
    });
}

/** El peso de un archivo en la línea de metadatos. `null` cuando no hay archivo (una nota). */
export function libSizeLabel(doc) {
    const size = doc && doc.file && doc.file.size;
    if (!size || typeof size !== 'number') return null;
    if (size < 1024) return `${size} B`;
    const kb = size / 1024;
    if (kb < 1024) return `${Math.round(kb)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
}

/** La duración de una nota de audio, `m:ss`. `null` si el backend no la sabe. */
export function libDurationLabel(doc) {
    const seconds = doc && doc.file && doc.file.duration_seconds;
    if (!seconds || typeof seconds !== 'number') return null;
    const total = Math.round(seconds);
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
